import { useEffect, useRef, useState } from "react";
import axiosInstant from "../../api";
import { proposalPricingPreviewEndpoint } from "../../api/endpoints";
import { ProposalPricingPreviewResponse, SelectedChannel } from "./types";

// Debounced server-side pricing preview for the wizard's tier cards —
// mirrors usePricingPreview (src/hooks/useOrderFormData.ts): 600ms debounce,
// signature-based change detection, last-write-wins.
export const useProposalPricingPreview = (
    objectiveId: number | null,
    secondaryObjectiveId: number | null,
    selectedChannels: SelectedChannel[],
    enabled: boolean,
    // RMN portal: point the preview at /rmn/proposals/preview and include
    // the supplier brand id in the payload. Defaults preserve the public
    // behavior exactly.
    options?: { endpoint?: string; brandId?: number },
) => {
    const [preview, setPreview] = useState<ProposalPricingPreviewResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const reqIdRef = useRef(0);
    const endpoint = options?.endpoint || proposalPricingPreviewEndpoint;
    const brandId = options?.brandId;

    const signature = JSON.stringify({
        objectiveId,
        secondaryObjectiveId,
        channelIds: selectedChannels.map((c) => c.id),
        endpoint,
        brandId,
    });

    useEffect(() => {
        if (!enabled || !objectiveId || selectedChannels.length === 0) {
            // Invalidate any in-flight request too — without the bump, a
            // response for the previous selection lands with a matching
            // reqId and resurrects pricing for channels no longer selected.
            reqIdRef.current += 1;
            setPreview(null);
            setLoading(false);
            return;
        }
        const reqId = reqIdRef.current + 1;
        reqIdRef.current = reqId;
        setLoading(true);
        const timer = setTimeout(async () => {
            try {
                const { data } = await axiosInstant.post<ProposalPricingPreviewResponse>(
                    endpoint,
                    {
                        objective_id: objectiveId,
                        secondary_objective_id: secondaryObjectiveId || undefined,
                        channel_ids: JSON.parse(signature).channelIds,
                        brand_id: brandId || undefined,
                    },
                );
                if (reqIdRef.current === reqId) setPreview(data);
            } catch {
                if (reqIdRef.current === reqId) setPreview(null);
            } finally {
                if (reqIdRef.current === reqId) setLoading(false);
            }
        }, 600);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [signature, enabled]);

    return { preview, loading };
};
